/**
 * `savedRulings` — the pure data behind the GM's saved-ruling library. Kept out of the components
 * so the id-minting rule and the one-render-path merge are unit-testable without a render harness —
 * the same split `slotAdmin.ts` makes for `GmSlots.tsx`.
 *
 * **Saved-ruling ids are caller-minted**, like every other `create_*` id in the app: the
 * reducer never generates one (`packages/domain/src/game/commands.ts`'s header). And like
 * a slot id — not like an opaque pilot/mech UUID — it is user-visible: it is what
 * `lvtt game delete-ruling --id <ref>` addresses and what the EDIT-surface manager shows,
 * so a readable slug beats a UUID. {@link mintSavedRulingId} therefore mirrors
 * `slotAdmin.ts`'s `mintSlotId` exactly.
 *
 * **The collision rule differs from slots, and the difference is the point.** `define_slot`
 * hard-rejects a duplicate id, so `mintSlotId` must dodge collisions before sending.
 * `save_ruling` on an existing id is a deliberate **overwrite** — that IS the edit gesture — so
 * minting only avoids clobbering an *unrelated* entry that happens to share a name. An
 * explicit edit passes the existing id straight through and never comes here at all.
 */
import type { SavedRuling } from "@lancer-vtt/domain";

/** lower-case + collapse non-alphanumerics to hyphens, trimmed (mirrors `slotAdmin.ts`'s `slugify`). */
function slugify(label: string): string {
  const s = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return s === "" ? "ruling" : s; // a label that slugifies to nothing (symbols only) still gets an id
}

/**
 * Mint a fresh, collision-free saved-ruling id from a typed label against the live set of
 * already-saved ids. Deterministic (same label + same existing set → same id), so it is
 * unit-testable without randomness.
 */
export function mintSavedRulingId(label: string, existingIds: readonly string[]): string {
  const base = slugify(label);
  if (!existingIds.includes(base)) return base;
  let n = 2;
  while (existingIds.includes(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

/**
 * Whether the saved-ruling management surface is available to this session role — GM-only,
 * mirroring `slotAdmin.ts`'s `canAdminSlots`. This is the UI affordance gate; the server's
 * `GameRoom.handle` gate (`isGm`) is the real wall, and `saveRuling`/`deleteSavedRuling` are
 * server-side no-ops for a non-GM socket regardless.
 */
export function canManageSavedRulings(role: "gm" | "player" | null): boolean {
  return role === "gm";
}

/**
 * Strip a `SavedRuling` down to what a *new* entry should carry when the GM keeps a ruling
 * they just fired ("save the one I just built").
 *
 * The **reason is captured as the entry's `defaultReason`** — the GM typed a real sentence at
 * fire time and that sentence is the best pre-fill this row will ever have. It stays a
 * *default*, not a fixed value: firing the saved row still focuses the reason field with it
 * pre-filled and the GM can type over it, because the same ruling gets stated differently on
 * different nights.
 */
export function savedRulingFrom(args: {
  id: string;
  label: string;
  status: string;
  reason: string;
  ruling: Omit<SavedRuling["ruling"], "reason">;
  expiry?: SavedRuling["expiry"];
}): SavedRuling {
  const { id, label, status, reason, ruling, expiry } = args;
  return {
    id,
    label,
    status,
    defaultReason: reason,
    // A fresh object rather than the caller's — the same no-aliasing discipline the reducer
    // enforces at the grant site. A library entry and the row it was built from must not
    // share a spec object, or editing one would silently rewrite the other.
    ruling: structuredClone(ruling),
    ...(expiry !== undefined ? { expiry } : {}),
  };
}
